/* eslint-disable class-methods-use-this */
import Extent from '@arcgis/core/geometry/Extent';
import { SketchType } from '../sketch-editor/graphics-handler';
import { Extents } from '../types/geo';

export default class LocalStoragePreferences {
  get lastMapBounds() {
    return this.get('lastMapBounds');
  }

  set lastMapBounds(mapBounds) {
    this.set('lastMapBounds', mapBounds);
  }

  get lastMapExtents(): Extents {
    let extents = this.get('lastMapExtents');

    // if there are no extents, attempt to migrate the old lastMapBounds
    if (!extents) {
      const bounds = this.lastMapBounds;

      if (bounds && bounds.length === 4) {
        const latitude = (bounds[0] + bounds[2]) / 2;
        const longitude = (bounds[1] + bounds[3]) / 2;

        extents = { latitude, longitude, zoom: 12 };
      }
    }

    return extents;
  }

  set lastMapExtents(extents: Extents) {
    this.set('lastMapExtents', extents);
  }

  get lastEditorMapExtents(): Extent {
    return this.get('lastEditorMapExtents', null);
  }

  setLastEditorMapExtents(extents: Extent) {
    if (extents) {
      this.set('lastEditorMapExtents', extents);
    }
  }

  removeLastEditorMapExtents() {
    this.set('lastEditorMapExtents', null);
  }

  get lastIssueMapExtents(): Extents {
    return this.get('lastIssueMapExtents', null);
  }

  setLastIssueMapExtents(extents: Extents) {
    this.set('lastIssueMapExtents', extents);
  }

  get issueEditorMapExtents(): Extents {
    return this.get('issueEditorMapExtents', null);
  }

  setIssueEditorMapExtents(extents: Extents) {
    if (extents) {
      this.set('issueEditorMapExtents', extents);
    }
  }

  get lastMapLayers() {
    return this.get('lastMapLayerIDs', []);
  }

  set lastMapLayers(layers) {
    this.set('lastMapLayerIDs', layers);
  }

  get basemapTilesHaveBeenReset() {
    return this.get('basemapTilesHaveBeenReset', false);
  }

  set basemapTilesHaveBeenReset(value: boolean) {
    this.set('basemapTilesHaveBeenReset', value);
  }

  get lastBasemap() {
    // last Esri Engine basemap
    return this.get('lastBasemapType');
  }

  set lastBasemap(map) {
    this.set('lastBasemapType', map);
  }

  get lastMap() {
    // last Google Engine basemap
    return this.get('lastMap', 'roadmap');
  }

  set lastMap(map: string) {
    this.set('lastMap', map);
  }

  get lastMode() {
    return this.get('lastMode', false);
  }

  set lastMode(map: string) {
    this.set('lastMode', map);
  }

  get lastSketchCreateTool() {
    return this.get('lastSketchCreateTool', 'point');
  }

  setSketchCreateTool(shape: SketchType) {
    this.set('lastSketchCreateTool', shape);
  }

  get lastUsedSnappingSettings() {
    return this.get('lastUsedSnappingSettings', null);
  }

  setLastUsedSnappingSettings(settings) {
    this.set('lastUsedSnappingSettings', settings);
  }

  viewKey(form, repeatableKey) {
    if (repeatableKey) {
      return `lastFormView:${form.id}:${repeatableKey}`;
    }

    return `lastFormView:${form.id}`;
  }

  getLastFormView(form, repeatableKey) {
    const value = this.get(this.viewKey(form, repeatableKey));
    return value != null ? JSON.parse(value) : null;
  }

  setLastFormView(form, repeatableKey, view) {
    this.set(this.viewKey(form, repeatableKey), view ? JSON.stringify(view) : null);
  }

  setHomePageFilter(key: string) {
    this.set('homePageRangeDateFilter', key);
  }

  getHomePageFilter() {
    return this.get('homePageRangeDateFilter', null);
  }

  getUserChips(chips) {
    return this.get(chips, null);
  }

  isSectionExpanded(section) {
    return this.get(`section:${section}:expanded`, true);
  }

  isTasksModalViewEnabled(section) {
    return this.get(`modal:${section}:enabled`, false);
  }

  isIssuesLocationEnabled() {
    return this.get('issueLocation:enabled', false);
  }

  setSectionExpanded(section, expanded) {
    if (expanded) {
      this.set(`section:${section}:expanded`, null);
    } else {
      this.set(`section:${section}:expanded`, false);
    }
  }

  getParamsFromQuery() {
    const { search } = window.location;
    if (!search) return {};

    const propsMap = {
      z: 'zoom',
      lat: 'latitude',
      lon: 'longitude',
    };

    const params = search.slice(1).split('&').reduce((acc, param) => {
      const [key, value] = param.split('=');
      const newKey = propsMap[key];
      if (newKey) {
        acc[newKey] = +value;
      }
      return acc;
    }, {});
    return params;
  }

  set(key: string, value: any) {
    if (value != null) {
      window.localStorage.setItem(key, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(key);
    }
  }

  get(key: string, defaultValue?: any) {
    const value = window.localStorage.getItem(key);
    const isLastMapExtents = key === 'lastMapExtents';
    const queryParams = isLastMapExtents ? this.getParamsFromQuery() : {};

    if (value != null) {
      const parsed = JSON.parse(value);
      return isLastMapExtents ? ({ ...parsed, ...queryParams }) : (parsed);
    }

    return defaultValue || null;
  }

  isSidenavSetupOpen() {
    return this.get('sidenav:setup:open', false);
  }

  isSidenavClosed() {
    return this.get('sidenavClosed', true);
  }

  useDrawerInEditor() {
    return this.get('use-drawer', false);
  }

  getPermissionChangePayloadLimit() {
    return this.get('permissionChangePayloadLimit', null);
  }

  setPermissionChangePayloadLimit(limit: number) {
    this.set('permissionChangePayloadLimit', limit);
  }

  getLastExportFormat() {
    return this.get('exports:format', 'csv');
  }

  setLastExportFormat(format: string) {
    this.set('exports:format', format);
  }

  getLastExportRecordLinkFormat() {
    return this.get('exports:recordLinkFormat', 'id');
  }

  setLastExportRecordLinkFormat(format: string) {
    this.set('exports:recordLinkFormat', format);
  }

  getLastExportMediaFormat() {
    return this.get('exports:mediaFormat', 'id');
  }

  setLastExportMediaFormat(format: string) {
    this.set('exports:mediaFormat', format);
  }
}
